import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  const lastNotificationRef = React.useRef({ type: null, message: null, at: 0 });

  const setNotificationSafely = useCallback((next) => {
    const now = Date.now();
    const last = lastNotificationRef.current;
    if (last && last.type === next.type && last.message === next.message && (now - last.at) < 1500) {
      return; // suppress duplicate within 1.5s
    }
    lastNotificationRef.current = { type: next.type, message: next.message, at: now };
    setNotification(next);
  }, []);

  const showSuccess = useCallback((message) => {
    setNotificationSafely({ type: 'success', message });
  }, [setNotificationSafely]);

  const showError = useCallback((message) => {
    setNotificationSafely({ type: 'error', message });
  }, [setNotificationSafely]);

  const showWarning = useCallback((message) => {
    setNotificationSafely({ type: 'warning', message });
  }, [setNotificationSafely]);

  const showInfo = useCallback((message) => {
    setNotificationSafely({ type: 'info', message });
  }, [setNotificationSafely]);

  // Info with action button (e.g., CTA)
  const showAction = useCallback((message, actionLabel, actionHandler, options = {}) => {
    setNotificationSafely({ 
      type: options.type || 'info', 
      message, 
      action: { label: actionLabel, onClick: actionHandler },
      duration: options.duration === undefined ? 0 : options.duration // default sticky
    });
  }, [setNotificationSafely]);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const value = {
    notification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showAction,
    hideNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};